// ============ Shader code snippets ============
let phongCode = `
vec3 phong(vec3 N, vec3 L, vec3 W, vec3 diffuse, vec4 specular) {
   vec3 R = 2. * N * dot(N,L) - L;
   return diffuse      * max(0., dot(N, L)) +
          specular.rgb * pow(max(0.,dot(R,-W)), specular.a);
}
`;

// ================= drawMesh ===================
export function drawMesh(mesh) {
    if (!currentCanvas || !currentCanvas._gl || !mesh) return;
    const gl = currentCanvas._gl;
    gl.bindBuffer(gl.ARRAY_BUFFER, currentCanvas._buffer);
    gl.bufferData(gl.ARRAY_BUFFER, mesh.data, gl.STATIC_DRAW);
    const vertexSize = currentCanvas._vertexSize || 6;
    const count = mesh.data.length / vertexSize;

    // Determine primitive type
    const mode = mesh.triangle_strip ? gl.TRIANGLE_STRIP : gl.TRIANGLES;

    gl.drawArrays(mode, 0, count);
}


// ============ Matrix utilities ============
const c = t => Math.cos(t);
const s = t => Math.sin(t);

export const identity = () => [1,0,0,0, 0,1,0,0, 0,0,1,0, 0,0,0,1];
export const move = (x,y,z) => [1,0,0,0, 0,1,0,0, 0,0,1,0, x,y,z,1];
export const turnX = t => [1,0,0,0, 0,c(t),s(t),0, 0,-s(t),c(t),0, 0,0,0,1];
export const turnY = t => [c(t),0,-s(t),0, 0,1,0,0, s(t),0,c(t),0, 0,0,0,1];
export const turnZ = t => [c(t),s(t),0,0, -s(t),c(t),0,0, 0,0,1,0, 0,0,0,1];
export const scale = (x,y,z) => [x,0,0,0, 0,y??x,0,0, 0,0,z??x,0, 0,0,0,1];
export const perspective = (x,y,z) => [1,0,0,x, 0,1,0,y??x, 0,0,1,z??x, 0,0,0,1];

// Multiply two matrices
export const mxm = (a,b) => {
   let m = [];
   for (let col = 0; col < 16; col += 4)
   for (let row = 0; row < 4; row++)
      m.push(a[row]*b[col] + a[row+4]*b[col+1] + a[row+8]*b[col+2] + a[row+12]*b[col+3]);
   return m;
}

// Invert a matrix
export const inverse = src => {
   let dst = [], det = 0, cofactor = (c, r) => {
      let s = (i, j) => src[c+i & 3 | (r+j & 3) << 2];
      return (c+r & 1 ? -1 : 1) * ((s(1,1)*(s(2,2)*s(3,3)-s(3,2)*s(2,3)))
                                  - (s(2,1)*(s(1,2)*s(3,3)-s(3,2)*s(1,3)))
                                  + (s(3,1)*(s(1,2)*s(2,3)-s(2,2)*s(1,3))));
   }
   for (let n = 0; n < 16; n++) dst.push(cofactor(n >> 2, n & 3));
   for (let n = 0; n < 4; n++) det += src[n] * dst[n << 2];
   for (let n = 0; n < 16; n++) dst[n] /= det;
   return dst;
}

// ============ WebGL setup ============
let currentCanvas = null;

export function setUniform(type, name, a, b, c) {
   try {
      const canvas = currentCanvas;
      if (!canvas || !canvas._gl || !canvas._gl.program) return;
      const gl = canvas._gl;
      const loc = gl.getUniformLocation(gl.program, name);
      if (!loc) return;
      gl.useProgram(gl.program);
      const fn = gl['uniform' + type];
      if (typeof fn !== 'function') return;
      fn.call(gl, loc, a, b, c);
   } catch (e) { console.warn('setUniform failed', e); }
}

export function gl_start(canvas, scene) {
   if (!canvas) return null;
   const ctx = canvas.getContext('webgl2', { alpha: true, antialias: true });
   if (!ctx) { 
      console.error('WebGL not supported'); 
      return null; 
   }
   
   canvas._gl = ctx;
   const gl = ctx;
   
   gl.clearColor(0,0,0,0);
   gl.enable(gl.DEPTH_TEST);

   gl.enable(gl.BLEND);
   // gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA);
   gl.blendFunc(gl.SRC_ALPHA, gl.ONE); // additive


   // Set shaders
   const program = gl.createProgram();
   
   function addShader(type, src) {
      const shader = gl.createShader(type);
      gl.shaderSource(shader, src);
      gl.compileShader(shader);
      if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
         console.error('Shader compile error:', gl.getShaderInfoLog(shader));
         return false;
      }
      gl.attachShader(program, shader);
      return true;
   }

   if (!addShader(gl.VERTEX_SHADER, scene.vertexShader)) return null;
   
   // Inject phong code into fragment shader
   let fragmentShader = scene.fragmentShader;
   let i = fragmentShader.indexOf('float') + 6;
   fragmentShader = fragmentShader.substring(0, i) + phongCode + fragmentShader.substring(i);
   
   if (!addShader(gl.FRAGMENT_SHADER, fragmentShader)) return null;
   
   gl.linkProgram(program);
   if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
      console.error('Program link error:', gl.getProgramInfoLog(program));
      return null;
   }
   
   gl.program = program;
   gl.useProgram(program);

   // Setup buffer
   const buf = gl.createBuffer();
   gl.bindBuffer(gl.ARRAY_BUFFER, buf);
   canvas._buffer = buf;
   canvas._vertexSize = scene.vertexSize || 6;
   canvas._mesh = scene.mesh || null;
   
   if (canvas._mesh && canvas._mesh.data) {
      gl.bufferData(gl.ARRAY_BUFFER, canvas._mesh.data, gl.STATIC_DRAW);
   }
   
   const aPos = gl.getAttribLocation(program, 'aPos');
   if (aPos >= 0) {
      gl.enableVertexAttribArray(aPos);
      gl.vertexAttribPointer(aPos, 3, gl.FLOAT, false, canvas._vertexSize * 4, 0);
   }
   
   const aNor = gl.getAttribLocation(program, 'aNor');
   if (aNor >= 0) {
      gl.enableVertexAttribArray(aNor);
      gl.vertexAttribPointer(aNor, 3, gl.FLOAT, false, canvas._vertexSize * 4, 12);
   }

   // Resize handling
   const resizeToDisplaySize = () => {
      const dpr = window.devicePixelRatio || 1;
      const w = Math.max(1, Math.round(canvas.clientWidth * dpr));
      const h = Math.max(1, Math.round(canvas.clientHeight * dpr));
      if (canvas.width !== w || canvas.height !== h) {
         canvas.width = w; 
         canvas.height = h;
         gl.viewport(0, 0, w, h);
      }
   };
   resizeToDisplaySize();

   // Render loop
   let start = performance.now();
   let raf = null;
   
   function render(now) {
      currentCanvas = canvas;
      gl.clear(gl.COLOR_BUFFER_BIT | gl.DEPTH_BUFFER_BIT);
      
      try { 
         if (scene.update) scene.update((now - start) / 1000); 
      } catch (e) { 
         console.error('Scene update error:', e); 
      }
      
      if (canvas._mesh && canvas._mesh.data) {
         const count = canvas._mesh.data.length / canvas._vertexSize;
         gl.drawArrays(gl.TRIANGLES, 0, count);
      }
      
      currentCanvas = null;
      raf = requestAnimationFrame(render);
   }
   
   raf = requestAnimationFrame(render);

   canvas.stopRender = () => {
      if (raf) cancelAnimationFrame(raf);
      currentCanvas = null;
   };

   return gl;
}